import { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { ChevronRight } from 'lucide-react'
import type { AdCampaign, AdSpot, AdStatus } from '../../store/slices/adsSlice.js'
import { campaignsApi, spotsApi } from '../../lib/adsApi.js'
import { attestationApi } from '../../lib/reportsApi.js'
import { useHasPermission } from '../../store/index.js'
import { formatDateShort, formatDurationShort, formatTime } from '../../lib/utils.js'
import { updateRangeSelection } from '../../lib/range-selection.js'
import type { CampaignPacing, CampaignUpcomingAiring } from '../../lib/adsApi.js'
import { BLOCKER_LABELS, PACING_LABELS, pacingTone, spotCount } from '../../lib/adsPacing.js'
import {
  CAMPAIGN_GROUP_LABELS,
  CAMPAIGN_GROUP_ORDER,
  countdownLabel,
  daysLeft,
  daysUntilStart,
  groupArchivedByMonth,
  groupCampaigns,
  remainingParts,
  slotShortLabels,
  type CampaignGroupKey,
} from '../../lib/adsCampaignGroups.js'
import { DeliveryBar } from './DeliveryBar.js'
import { SpotMediaSection } from './SpotMediaSection.js'
import { MediaBrowserModal } from '../programs/MediaBrowserModal.js'
import { NewCampaignModal } from './NewCampaignModal.js'
import { Button } from '../ui/button.js'
import { Checkbox } from '../ui/checkbox.js'

// ─── Status labels & colors ───────────────────────────────────────────────────

const STATUS_LABELS: Record<AdStatus, string> = {
  DRAFT:     'Brouillon',
  ACTIVE:    'Active',
  PAUSED:    'Pausée',
  COMPLETED: 'Terminée',
  CANCELLED: 'Annulée',
}

type AdsTone = 'success' | 'warning' | 'danger' | 'info' | 'muted'

function statusTone(status: AdStatus): AdsTone {
  if (status === 'ACTIVE') return 'success'
  if (status === 'PAUSED') return 'warning'
  if (status === 'COMPLETED') return 'info'
  if (status === 'CANCELLED') return 'danger'
  return 'muted'
}

/**
 * Un spot inexploitable a l'antenne : pas de fichier associe, ou une duree que
 * FFprobe n'a pas su detecter. Le cas etait deja refuse a la saisie, mais rien
 * ne le signalait sur un spot deja enregistre.
 */
function isSpotIncomplete(spot: AdSpot): boolean {
  return !spot.filePath || !(spot.durationSec > 0)
}

// ══════════════════════════════════════════════════════════════════
// CampaignList — colonne gauche
// ══════════════════════════════════════════════════════════════════

interface CampaignListProps {
  campaigns:        AdCampaign[]
  selectedId:       string | null
  onSelect:         (c: AdCampaign) => void
  onNew:            () => void
  /** Suppression définitive, réservée au filtre « Archivées ». */
  onDeleteMany:     (ids: string[]) => Promise<string[]>
  /** Tenue d'engagement par campagne ; absente tant que l'inventaire charge. */
  pacingById:       Map<string, CampaignPacing>
  /** Verdict imposé par la barre d'attention, cumulé avec le filtre de statut. */
  verdictFilter:    CampaignPacing['verdict'] | null
  /** Filtre courant ; « archived » bascule la liste en vue archives. */
  view:             CampaignView
  onViewChange:     (view: CampaignView) => void
  /** Instant de référence des compteurs ; injecté pour rendre la vue testable. */
  now?:             number
}

/**
 * Les filtres de la liste. « Archivées » n'est pas un statut de plus : il
 * change la source de données (le serveur ne renvoie les campagnes closes que
 * sur demande) et le jeu d'actions offert.
 */
export type CampaignView = 'ALL' | 'ACTIVE' | 'DRAFT' | 'PAUSED' | 'ARCHIVED'

const VIEW_LABELS: Record<CampaignView, string> = {
  ALL:      'Toutes',
  ACTIVE:   'Active',
  DRAFT:    'Brouillon',
  PAUSED:   'Pausée',
  ARCHIVED: 'Archivées',
}

const VIEW_ORDER: CampaignView[] = ['ALL', 'ACTIVE', 'DRAFT', 'PAUSED', 'ARCHIVED']

export const CampaignList = memo(function CampaignList({
  campaigns, selectedId, onSelect, onNew, onDeleteMany, pacingById, verdictFilter,
  view, onViewChange, now = Date.now(),
}: CampaignListProps) {
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<Set<string>>(() => new Set())
  const [deleting, setDeleting] = useState(false)
  const selectionAnchorRef = useRef<string | null>(null)
  const archived = view === 'ARCHIVED'

  const filtered = useMemo(() => campaigns.filter(campaign => {
    // En vue archives, le serveur a déjà restreint la liste : filtrer de
    // nouveau sur le statut masquerait les campagnes annulées, qui sont
    // justement celles qu'on vient y chercher.
    if (!archived && view !== 'ALL' && campaign.status !== view) return false
    if (!verdictFilter) return true
    return pacingById.get(campaign.id)?.verdict === verdictFilter
  }), [campaigns, view, archived, verdictFilter, pacingById])

  const groups         = useMemo(() => groupCampaigns(filtered, now), [filtered, now])
  const archivedMonths = useMemo(() => (archived ? groupArchivedByMonth(filtered) : []), [archived, filtered])

  const filteredIds = useMemo(() => filtered.map(campaign => campaign.id), [filtered])
  const allSelected = filteredIds.length > 0 && filteredIds.every(id => selectedCampaignIds.has(id))
  const someSelected = filteredIds.some(id => selectedCampaignIds.has(id))

  useEffect(() => {
    const visibleIds = new Set(filteredIds)
    setSelectedCampaignIds(previous => {
      const next = new Set([...previous].filter(id => visibleIds.has(id)))
      return next.size === previous.size ? previous : next
    })
    if (selectionAnchorRef.current && !visibleIds.has(selectionAnchorRef.current)) {
      selectionAnchorRef.current = null
    }
  }, [filteredIds])

  const handleCheck = useCallback((campaignId: string, checked: boolean, shiftKey: boolean) => {
    setSelectedCampaignIds(previous => shiftKey
      ? updateRangeSelection(filteredIds, previous, selectionAnchorRef.current, campaignId, checked)
      : (() => {
          const next = new Set(previous)
          if (checked) next.add(campaignId)
          else next.delete(campaignId)
          return next
        })())
    if (!shiftKey) selectionAnchorRef.current = campaignId
  }, [filteredIds])

  const handleCheckAll = useCallback(() => {
    setSelectedCampaignIds(allSelected ? new Set() : new Set(filteredIds))
    selectionAnchorRef.current = null
  }, [allSelected, filteredIds])

  const handleClearSelection = useCallback(() => {
    setSelectedCampaignIds(new Set())
    selectionAnchorRef.current = null
  }, [])

  const handleBulkDelete = useCallback(async () => {
    if (selectedCampaignIds.size === 0) return
    setDeleting(true)
    try {
      const deletedIds = await onDeleteMany([...selectedCampaignIds])
      setSelectedCampaignIds(previous => {
        const next = new Set(previous)
        deletedIds.forEach(id => next.delete(id))
        return next
      })
      selectionAnchorRef.current = null
    } finally {
      setDeleting(false)
    }
  }, [selectedCampaignIds, onDeleteMany])

  const renderRow = (campaign: AdCampaign, group: CampaignGroupKey | 'archived') => (
    <CampaignRow
      key={campaign.id}
      campaign={campaign}
      group={group}
      now={now}
      pacing={pacingById.get(campaign.id) ?? null}
      active={campaign.id === selectedId}
      checked={selectedCampaignIds.has(campaign.id)}
      selectable={archived}
      onClick={() => onSelect(campaign)}
      onCheck={handleCheck}
    />
  )

  return (
    // La largeur et la bordure droite sont contrôlées par le parent (AdsView)
    // pour permettre le redimensionnement de la colonne via ResizeHandle.
    <div className="campaign-list">

      {/* Toolbar */}
      <div className="campaign-list__toolbar">
        <div className="campaign-list__filters" aria-label="Filtres de campagne">
          {VIEW_ORDER.map(key => (
            <button
              key={key}
              type="button"
              onClick={() => onViewChange(key)}
              className={view === key ? 'campaign-filter is-active' : 'campaign-filter'}
              aria-pressed={view === key}
            >
              {VIEW_LABELS[key]}
            </button>
          ))}
        </div>
      </div>

      {/* Compteur + actions. La sélection multiple et la suppression ne vivent
          qu'en vue archives : le catalogue courant n'offre que l'archivage,
          réversible. */}
      <div className="campaign-list__summary">
        {archived && (
          <Checkbox
            className="catalog-selection-checkbox"
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            disabled={filtered.length === 0 || deleting}
            onClick={handleCheckAll}
            aria-label="Sélectionner toutes les campagnes affichées"
          />
        )}
        <span role="status">
          {filtered.length} campagne{filtered.length !== 1 ? 's' : ''}
        </span>
        {!archived && (
          <button onClick={onNew} className="tp-button tp-button--info campaign-list__new">
            + Nouvelle
          </button>
        )}
      </div>

      {/* Barre de sélection des archives : elle n'apparaît qu'une fois quelque
          chose de sélectionné, et porte la seule action destructive de l'écran. */}
      {archived && selectedCampaignIds.size > 0 && (
        <div className="campaign-selection-bar" role="group" aria-label="Sélection à supprimer">
          <span className="campaign-selection-bar__count">
            {selectedCampaignIds.size} sélectionnée{selectedCampaignIds.size > 1 ? 's' : ''}
          </span>
          <button type="button" className="campaign-selection-bar__clear" onClick={handleClearSelection}>
            Désélectionner
          </button>
          {/* Double-clic : la suppression est définitive, un clic unique la
              rendrait atteignable par mégarde sur une liste qu'on parcourt. */}
          <Button
            type="button"
            variant="destructive"
            size="xs"
            disabled={deleting}
            onDoubleClick={() => void handleBulkDelete()}
            title="Double-cliquez pour supprimer définitivement"
          >
            {deleting ? 'Suppression…' : 'Supprimer définitivement'}
          </Button>
        </div>
      )}

      {/* Liste */}
      <div className="campaign-list__rows">
        {archived
          ? archivedMonths.map(month => (
              <section key={month.key} className="campaign-group">
                <h3 className="campaign-group__title">{month.label}</h3>
                {month.campaigns.map(campaign => renderRow(campaign, 'archived'))}
              </section>
            ))
          : CAMPAIGN_GROUP_ORDER.filter(key => groups[key].length > 0).map(key => (
              <section key={key} className="campaign-group">
                <h3 className="campaign-group__title">
                  {CAMPAIGN_GROUP_LABELS[key]}
                  <span className="campaign-group__count">{groups[key].length}</span>
                </h3>
                {groups[key].map(campaign => renderRow(campaign, key))}
              </section>
            ))}
        {filtered.length === 0 && (
          <div className="tp-empty-state campaign-list__empty">
            {archived ? 'Aucune campagne archivée' : 'Aucune campagne'}
          </div>
        )}
      </div>
    </div>
  )
})

const CampaignRow = memo(function CampaignRow({ campaign, group, now, pacing, active, checked, selectable, onClick, onCheck }: {
  campaign: AdCampaign
  group: CampaignGroupKey | 'archived'
  now: number
  pacing: CampaignPacing | null
  active: boolean
  checked: boolean
  selectable: boolean
  onClick: () => void
  onCheck: (campaignId: string, checked: boolean, shiftKey: boolean) => void
}) {
  // La liste ne charge pas systématiquement les spots : sans eux on n'affiche
  // simplement pas le marqueur, plutôt que d'annoncer « 0 à corriger » à tort.
  const rowIncomplete = (campaign.spots ?? []).filter(isSpotIncomplete).length
  const slots = slotShortLabels(campaign)
  const remaining = remainingParts(daysLeft(campaign, now))

  return (
    <div className={`campaign-row campaign-row--${group}${checked ? ' is-selected' : ''}${active ? ' is-active' : ''}`}>
      {selectable && (
        <Checkbox
          className="catalog-selection-checkbox"
          checked={checked}
          onClick={event => {
            event.stopPropagation()
            onCheck(campaign.id, !checked, event.shiftKey)
          }}
          aria-label={`Sélectionner la campagne ${campaign.name}`}
        />
      )}

      {/* Compte à rebours des campagnes à venir : c'est le chiffre qu'on vient
          chercher sur cette section, il passe donc devant le nom. */}
      {group === 'upcoming' && (
        <div className="campaign-countdown" aria-hidden="true">
          <span className="campaign-countdown__value">{countdownLabel(daysUntilStart(campaign, now))}</span>
          <span className="campaign-countdown__caption">avant diffusion</span>
        </div>
      )}

      <button type="button" onClick={onClick} className="campaign-row__select" aria-current={active ? 'true' : undefined}>
        <div className="campaign-row__header">
          <div className="campaign-row__name">
            {campaign.name}
          </div>
          {/* Le statut n'est montré que lorsqu'il apprend quelque chose : sur
              une campagne en cours, « Active » est le cas normal et n'occupe la
              ligne pour rien. Une pause, elle, veut dire que rien ne part à
              l'antenne — elle doit rester visible.
              Un point suivi du mot, plutôt qu'une pastille pleine : la ligne
              porte déjà des tranches horaires encadrées, une seconde forme
              fermée à côté d'elles rendait l'ensemble compact et bruyant. */}
          {!(group === 'current' && campaign.status === 'ACTIVE') && (
            <span className={`campaign-row__status is-${statusTone(campaign.status)}`}>
              <span className="campaign-row__status-dot" aria-hidden="true" />
              {STATUS_LABELS[campaign.status]}
            </span>
          )}
          {rowIncomplete > 0 && (
            <span
              className="campaign-row__warning"
              title={`${rowIncomplete} spot(s) sans média exploitable`}
              aria-label={`${rowIncomplete} spot(s) sans média exploitable`}
            >
              !
            </span>
          )}
        </div>
        <div className="campaign-row__meta">
          {campaign.advertiser?.name ?? '—'}
          {group === 'archived' && ` · clos le ${formatDateShort(campaign.archivedAt ?? campaign.endDate)}`}
          {group === 'upcoming' && ` · ${formatDateShort(campaign.startDate)} → ${formatDateShort(campaign.endDate)}`}
        </div>

        {/* Une campagne en cours se lit par ses tranches horaires et le temps
            qu'il lui reste : en télévision locale c'est la période de passage
            qui est vendue, le nombre exact de spots varie. Le compteur reste
            lisible, mais il ne commande plus la ligne. */}
        {group === 'current' ? (
          <>
            <div className="campaign-row__airtime">
              <div className="campaign-slot-chips">
                {slots.length > 0
                  ? slots.map((slot, index) => <span key={index} className="campaign-slot-chip">{slot}</span>)
                  : <span className="campaign-slot-chip is-all">Tous créneaux</span>}
              </div>
              {/* Chiffre et légende empilés, comme le compte à rebours des
                  campagnes à venir : sur une seule ligne, « 12 jours restants »
                  chasse les tranches horaires hors de la colonne. */}
              <span className="campaign-row__remaining">
                {remaining.value !== null && (
                  <span className="campaign-row__remaining-value">{remaining.value}</span>
                )}
                <span className={`campaign-row__remaining-caption${remaining.value === null ? ' is-last' : ''}`}>
                  {remaining.caption}
                </span>
              </span>
            </div>
            {pacing && <DeliveryBar pacing={pacing} />}
            <div className="campaign-row__footnote">
              {pacing
                ? <span className={`campaign-row__verdict is-${pacingTone(pacing.verdict)}`}>{PACING_LABELS[pacing.verdict]}</span>
                : <span className="campaign-row__verdict is-muted">Inventaire…</span>}
              <span className="campaign-row__counts">
                {pacing ? `${pacing.placedSpots}/${pacing.soldSpots}` : `${campaign.spotsPlaced}/${campaign.spotsTotal}`} spots
              </span>
            </div>
          </>
        ) : group === 'upcoming' ? (
          <div className="campaign-slot-chips">
            {slots.length > 0
              ? slots.map((slot, index) => <span key={index} className="campaign-slot-chip">{slot}</span>)
              : <span className="campaign-slot-chip is-all">Tous créneaux</span>}
          </div>
        ) : (
          <div className="campaign-row__footnote">
            <span className="campaign-row__counts">
              {campaign.spotsPlaced}/{campaign.spotsTotal} spots
            </span>
          </div>
        )}
      </button>
    </div>
  )
})

/**
 * La phrase que la régie lirait à voix haute. Elle nomme la date butoir et la
 * cause du blocage : un verdict seul ne dit pas quoi faire, et c'est ce qui
 * rendait l'ancien « 62 % » inutilisable.
 */
function pacingSentence(pacing: CampaignPacing, campaign: AdCampaign): string {
  const deadline = formatDateShort(campaign.endDate)
  const reach    = `${spotCount(pacing.reachableSpots)} posables d’ici le ${deadline}`

  switch (pacing.verdict) {
    case 'DONE':
      return `Les ${pacing.soldSpots} passages vendus sont posés.`
    case 'NOT_STARTED':
      return `La campagne démarre le ${formatDateShort(campaign.startDate)} : ${spotCount(pacing.remainingSpots)} à poser.`
    case 'ENDED_SHORT':
      return `Période close le ${deadline} : ${spotCount(pacing.remainingSpots)} vendus n’ont pas été posés.`
    case 'UNREACHABLE': {
      const cause = pacing.blocker ? ` — ${BLOCKER_LABELS[pacing.blocker]}` : ''
      return `${spotCount(pacing.remainingSpots)} restent à poser pour seulement ${reach}${cause}.`
    }
    case 'TIGHT':
      return `${spotCount(pacing.remainingSpots)} restent à poser pour ${reach} : la marge ne survit pas à un programme rallongé.`
    default:
      return `${spotCount(pacing.remainingSpots)} restent à poser, pour ${reach}.`
  }
}

// ══════════════════════════════════════════════════════════════════
// CampaignDetail — colonne centrale
// ══════════════════════════════════════════════════════════════════

interface CampaignDetailProps {
  campaign:  AdCampaign
  pacing:    CampaignPacing | null
  onUpdated: (c: AdCampaign) => void
  onDeleted: () => void
}

export const CampaignDetail = memo(function CampaignDetail({ campaign, pacing, onUpdated, onDeleted }: CampaignDetailProps) {
  const canPlace = useHasPermission('GRID_ITEM_EDIT')
  const canExportReports = useHasPermission('REPORTS_VIEW_EXPORT')
  const [spots,       setSpots]       = useState<AdSpot[]>([])
  const [upcoming,    setUpcoming]    = useState<CampaignUpcomingAiring[]>([])
  const [placing,     setPlacing]     = useState(false)
  const [addingSpot,  setAddingSpot]  = useState(false)
  const [editing,     setEditing]     = useState(false)
  const incompleteSpots = spots.filter(isSpotIncomplete).length
  const archived = campaign.archivedAt !== null

  // Charge les spots de la campagne
  useEffect(() => {
    spotsApi.listByCampaign(campaign.id).then(setSpots).catch(() => setSpots([]))
  }, [campaign.id])

  // Prochains passages : lecture séparée des spots, pour que la fiche
  // s'affiche sans attendre une requête qui balaie les placements à venir.
  useEffect(() => {
    campaignsApi.upcoming(campaign.id).then(setUpcoming).catch(() => setUpcoming([]))
  }, [campaign.id, campaign.spotsPlaced])

  const handleAutoPlace = async () => {
    setPlacing(true)
    try {
      const result = await campaignsApi.autoPlace(campaign.id)
      const updated = await campaignsApi.getById(campaign.id)
      onUpdated(updated)
    } catch (err: any) {
      alert(err.message)
    } finally {
      setPlacing(false)
    }
  }

  const handleStatusChange = async (status: AdStatus) => {
    const updated = await campaignsApi.update(campaign.id, { status })
    onUpdated(updated)
  }

  const handleAddSpot = async (data: { title: string; durationSec: number; filePath: string }) => {
    const spot = await spotsApi.createInCampaign(campaign.id, data)
    setSpots(prev => [...prev, spot])
    setAddingSpot(false)
    // Recharge la campagne pour màj spotsTotal
    const updated = await campaignsApi.getById(campaign.id)
    onUpdated(updated)
  }

  const [attesting, setAttesting] = useState(false)

  const refreshCampaign = useCallback(async () => {
    const updated = await campaignsApi.getById(campaign.id)
    onUpdated(updated)
  }, [campaign.id, onUpdated])

  // Attestation de diffusion publicitaire : couvre les spots de la campagne
  // réellement passés à l'antenne (le backend s'appuie sur les as-run). Sans
  // bornage de dates, toutes les diffusions de la campagne sont certifiées.
  const handleAttestation = useCallback(async () => {
    setAttesting(true)
    try {
      const blob = await attestationApi.downloadAdsPdf({ channelId: campaign.channelId, campaignId: campaign.id })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `attestation_${campaign.name.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase() || 'campagne'}.pdf`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      alert(err?.message ?? 'Échec de la génération de l’attestation')
    } finally {
      setAttesting(false)
    }
  }, [campaign.channelId, campaign.id, campaign.name])

  const handleDeleteSpot = useCallback(async (spotId: string) => {
    await spotsApi.delete(spotId)
    setSpots(previous => previous.filter(spot => spot.id !== spotId))
    await refreshCampaign()
  }, [refreshCampaign])

  // La réponse du remplacement ne porte pas `_count` ni la présence : on
  // fusionne pour garder ce que la liste sait déjà du spot.
  const handleSpotReplaced = useCallback((updated: AdSpot) => {
    setSpots(previous => previous.map(spot => spot.id === updated.id ? { ...spot, ...updated } : spot))
  }, [])

  const handleArchive = useCallback(async () => {
    const updated = await campaignsApi.archive(campaign.id)
    onUpdated(updated)
  }, [campaign.id, onUpdated])

  const handleRestore = useCallback(async () => {
    const updated = await campaignsApi.restore(campaign.id)
    onUpdated(updated)
  }, [campaign.id, onUpdated])

  // Suppression définitive : elle détruit la campagne et ses placements. Un
  // double-clic plutôt qu'un dialogue — le geste est volontaire, pas
  // réversible, et l'opérateur ne lit plus les boîtes de confirmation.
  const handleDeleteForever = useCallback(async () => {
    await campaignsApi.delete(campaign.id)
    onDeleted()
  }, [campaign.id, onDeleted])

  return (
    <div className="campaign-detail">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <header className="campaign-detail__header">
        <div className="campaign-detail__identity">
          <div className="campaign-detail__copy">
            <h2 className="campaign-detail__title">{campaign.name}</h2>
            <div className="campaign-detail__meta">
              {campaign.advertiser?.name ?? '—'} · {formatDateShort(campaign.startDate)} → {formatDateShort(campaign.endDate)}
              {archived && campaign.archivedAt && ` · archivée le ${formatDateShort(campaign.archivedAt)}`}
            </div>
          </div>
          <span className={`ads-status ads-status--${statusTone(campaign.status)}`}>
            {STATUS_LABELS[campaign.status]}
          </span>
        </div>

        {/* Actions. Une campagne archivée ne se modifie plus et ne se place
            plus : seules la restauration et la suppression définitive restent. */}
        <div className="campaign-detail__actions">
          {archived ? (
            <>
              <ActionBtn label="Restaurer" onClick={() => void handleRestore()} variant="primary" />
              {canExportReports && <ActionBtn label={attesting ? 'Attestation…' : 'Attestation'} onClick={handleAttestation} disabled={attesting} />}
              <ActionBtn
                label="Supprimer définitivement"
                onDoubleClick={() => void handleDeleteForever()}
                title="Double-cliquez pour supprimer définitivement"
                variant="danger"
              />
            </>
          ) : (
            <>
              {canPlace && <ActionBtn label={placing ? 'Placement…' : 'Placement auto'} onClick={handleAutoPlace} disabled={placing} variant="primary" />}
              <ActionBtn label="Modifier" onClick={() => setEditing(true)} />
              {canExportReports && <ActionBtn label={attesting ? 'Attestation…' : 'Attestation'} onClick={handleAttestation} disabled={attesting} />}
              {campaign.status === 'DRAFT'  && <ActionBtn label="Activer"  onClick={() => handleStatusChange('ACTIVE')}  />}
              {campaign.status === 'ACTIVE' && <ActionBtn label="Pauser"   onClick={() => handleStatusChange('PAUSED')}  />}
              {campaign.status === 'PAUSED' && <ActionBtn label="Reprendre" onClick={() => handleStatusChange('ACTIVE')} />}
              <ActionBtn label="Archiver" onClick={() => void handleArchive()} />
            </>
          )}
        </div>
      </header>

      {/* Modal d'édition */}
      {editing && (
        <NewCampaignModal
          channelId={campaign.channelId}
          advertisers={[]}
          campaign={campaign}
          onUpdated={updated => { onUpdated(updated); setEditing(false) }}
          onClose={() => setEditing(false)}
        />
      )}

      <div className="campaign-detail__scroll">

        {/* ── Alerte média : un spot injouable est un incident d'antenne, pas
             une métrique parmi d'autres. ─────────────────────────────────── */}
        {incompleteSpots > 0 && (
          <div className="campaign-alert" role="alert">
            <strong>{spotCount(incompleteSpots)} sans média exploitable.</strong>{' '}
            Ces passages ne partiront pas à l’antenne : associez un fichier dans la liste des spots.
          </div>
        )}

        {/* ── Engagement ───────────────────────────────────────────────────── */}
        <Section title="Engagement">
          {pacing ? (
            <>
              <DeliveryBar pacing={pacing} detailed />
              <p className={`campaign-pacing is-${pacingTone(pacing.verdict)}`}>
                {pacingSentence(pacing, campaign)}
              </p>
            </>
          ) : (
            <div className="tp-empty-state campaign-section__empty">Lecture de l’inventaire…</div>
          )}
        </Section>

        {/* ── Créneaux cibles ───────────────────────────────────────────────── */}
        <Section title="Créneaux cibles">
          {campaign.targetSlots.length > 0 ? (
            <div className="campaign-slot-list">
              {campaign.targetSlots.map((s, i) => (
                <span key={i} className="campaign-slot">
                  {s}
                </span>
              ))}
            </div>
          ) : (
            <div className="tp-empty-state campaign-section__empty">Tous créneaux</div>
          )}
        </Section>

        {/* ── Rotation ──────────────────────────────────────────────────────
             Elle ne s'applique qu'aux poses qui ne nomment pas le spot :
             décrochage préfait, comblage. Une pose manuelle diffuse ce qui est
             nommé, la rotation n'y change rien — le dire évite de croire que ce
             réglage gouverne toute la campagne. ─────────────────────────── */}
        {spots.length > 1 && (
          <Section title="Rotation des spots">
            <p className="campaign-rotation">
              {campaign.pickStrategy === 'RANDOM'
                ? 'Tirage au sort parmi les spots diffusables.'
                : 'Du moins diffusé au plus diffusé : les spots tournent à parts égales.'}
            </p>
            <p className="campaign-rotation__note">
              S’applique quand la campagne est posée sans nommer de spot (décrochage préfait, comblage).
            </p>
          </Section>
        )}

        {/* ── Prochains passages ─────────────────────────────────────────────
             Ce qui va réellement partir, par opposition au seul compte de ce
             qui est posé. ───────────────────────────────────────────────── */}
        <Section title="Prochains passages à l’antenne">
          {upcoming.length > 0 ? (
            <div className="campaign-upcoming">
              {upcoming.map(airing => (
                <div key={airing.placementId} className="campaign-upcoming__item">
                  <span className="campaign-upcoming__time">{formatTime(airing.scheduledAt)}</span>
                  <span className="campaign-upcoming__date">{formatDateShort(airing.scheduledAt)}</span>
                  <span className="campaign-upcoming__spot">{airing.spotTitle}</span>
                  <span className="campaign-upcoming__duration">{formatDurationShort(airing.durationSec)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="tp-empty-state campaign-section__empty">Aucun passage à venir en grille</div>
          )}
        </Section>

        {/* ── Spots ────────────────────────────────────────────────────────── */}
        <Section
          title={`Spots (${spots.length})`}
          action={<button onClick={() => setAddingSpot(v => !v)} className="campaign-section__action">+ Ajouter</button>}
        >
          {addingSpot && <AddSpotForm onAdd={handleAddSpot} onCancel={() => setAddingSpot(false)} />}
          <div className="campaign-spots">
            {spots.map((spot, i) => (
              <SpotRow
                key={spot.id}
                spot={spot}
                isLast={i === spots.length - 1}
                onDelete={() => void handleDeleteSpot(spot.id)}
                onReplaced={handleSpotReplaced}
                archived={archived}
              />
            ))}
            {spots.length === 0 && !addingSpot && (
              <div className="tp-empty-state campaign-section__empty">Aucun spot associé</div>
            )}
          </div>
        </Section>
      </div>
    </div>
  )
})

// ─── Spot row ─────────────────────────────────────────────────────────────────

/**
 * Un clic sur la ligne la déplie sur la carte « Média associé », comme une
 * fiche programme. « Suppr. » reste hors du bouton de dépliage : deux
 * contrôles imbriqués ne sont ni cliquables ni lisibles au clavier.
 */
const SpotRow = memo(function SpotRow({ spot, isLast, onDelete, onReplaced, archived }: {
  spot: AdSpot
  isLast: boolean
  onDelete: () => void
  onReplaced: (spot: AdSpot) => void
  archived: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const incomplete = isSpotIncomplete(spot)
  const reason = !spot.filePath
    ? 'Aucun fichier associé'
    : 'Durée non détectée par FFprobe'

  return (
    <div className={`spot-row${isLast ? ' is-last' : ''}${incomplete ? ' is-incomplete' : ''}${expanded ? ' is-expanded' : ''}`}>
      <div className="spot-row__head">
        <button
          type="button"
          className="spot-row__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          <ChevronRight className="spot-row__chevron" aria-hidden="true" />
          <span className="spot-row__copy">
            <span className="spot-row__title">
              {spot.title}
              {incomplete && <span className="spot-row__flag">À CORRIGER</span>}
            </span>
            <span className="spot-row__meta">
              {incomplete
                ? <span className="spot-row__reason">{reason} — ce spot ne peut pas être placé</span>
                : (
                  <>
                    {formatDurationShort(spot.durationSec)}
                    {spot.filePath && <span className="spot-row__file">{spot.filePath.split('/').pop()}</span>}
                  </>
                )}
            </span>
          </span>
        </button>
        <button type="button" onClick={onDelete} className="tp-button tp-button--danger-outline spot-row__delete">Suppr.</button>
      </div>
      {expanded && (
        <SpotMediaSection
          spot={spot}
          onReplaced={onReplaced}
          disabled={archived}
          disabledReason={archived ? 'Campagne archivée : restaurez-la pour changer le fichier de ses spots.' : undefined}
        />
      )}
    </div>
  )
})

// ─── Formulaire ajout spot ────────────────────────────────────────────────────

/**
 * Ajout d'un spot : la durée est exclusivement détectée depuis le fichier média
 * (analyse durée réelle via FFprobe côté backend). Pas de saisie manuelle pour
 * éviter les imprécisions à l'antenne.
 */
const AddSpotForm = memo(function AddSpotForm({ onAdd, onCancel }: { onAdd: (d: any) => void; onCancel: () => void }) {
  const [title,           setTitle]           = useState('')
  const [filePath,        setFilePath]        = useState('')
  const [fileName,        setFileName]        = useState('')
  const [detectedSec,     setDetectedSec]     = useState<number | null>(null)
  const [showBrowser,     setShowBrowser]     = useState(false)
  const [error,           setError]           = useState<string | null>(null)

  const canSubmit = !!title && !!filePath && detectedSec != null && detectedSec > 0

  const handleMediaSelected = (result: { filePath: string; fileName: string; durationSec: number | null; error?: string; status?: string }) => {
    if (result.durationSec == null || result.durationSec <= 0) {
      const detail = result.error
        ? `Détail : ${result.error}`
        : 'Vérifiez que FFprobe est installé (FFPROBE_PATH) et que le fichier n\'est pas corrompu.'
      setError(`Durée non détectée pour "${result.fileName}". ${detail}`)
      setShowBrowser(false)
      return
    }
    setFilePath(result.filePath)
    setFileName(result.fileName)
    setDetectedSec(Math.round(result.durationSec))
    if (!title) setTitle(result.fileName.replace(/\.[^.]+$/, ''))
    setError(null)
    setShowBrowser(false)
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    onAdd({ title, durationSec: detectedSec, filePath })
  }

  return (
    <div className="add-spot-form">

      <input
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Titre du spot"
        className="tp-input add-spot-form__title"
      />

      {/* Sélection du média */}
      {!filePath ? (
        <button
          onClick={() => setShowBrowser(true)}
          className="add-spot-form__media-picker"
        >
          + Choisir un fichier média
        </button>
      ) : (
        <div className="add-spot-form__media">
          <div className="add-spot-form__media-copy">
            <div className="add-spot-form__file">
              {fileName || filePath.split('/').pop()}
            </div>
            <div className="add-spot-form__duration">
              Durée détectée : <strong>
                {detectedSec != null ? `${detectedSec}s` : '—'}
              </strong>
            </div>
          </div>
          <button
            onClick={() => setShowBrowser(true)}
            title="Changer de fichier"
            className="tp-button add-spot-form__change"
          >
            Changer
          </button>
        </div>
      )}

      {error && (
        <div className="tp-feedback tp-feedback--error add-spot-form__error" role="alert">
          {error}
        </div>
      )}

      <div className="add-spot-form__actions">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="tp-button tp-button--success-outline"
        >
          Ajouter
        </button>
        <button
          onClick={onCancel}
          className="tp-button"
        >
          Annuler
        </button>
      </div>

      {showBrowser && (
        <MediaBrowserModal
          onSelect={handleMediaSelected}
          onClose={() => setShowBrowser(false)}
        />
      )}
    </div>
  )
})

// ─── Helpers partagés ─────────────────────────────────────────────────────────

const Section = memo(function Section({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section className="campaign-section">
      <div className="campaign-section__header">
        <h3 className="campaign-section__title">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  )
})

const ActionBtn = memo(function ActionBtn({ label, onClick, onDoubleClick, title, variant, disabled }: {
  label: string
  onClick?: () => void
  /** Actions irréversibles : le geste doit être double, jamais un clic isolé. */
  onDoubleClick?: () => void
  title?: string
  variant?: 'primary' | 'danger'
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      disabled={disabled}
      title={title}
      className={`tp-button campaign-action${variant === 'primary' ? ' tp-button--primary' : variant === 'danger' ? ' tp-button--danger-outline' : ''}`}
      data-state={disabled && variant === 'primary' ? 'loading' : undefined}
    >
      {label}
    </button>
  )
})
